import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import Layout from "@/pages/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Settings from "@/pages/Settings";
import Run from "@/pages/Run";
import RunHistory from "@/pages/RunHistory";
import RunDetail from "@/pages/RunDetail";
import AuthCallback from "@/pages/AuthCallback";
import FnbPoLanding from "@/pages/fnb/FnbPoLanding";
import FnbPoUploads from "@/pages/fnb/FnbPoUploads";
import FnbPoItems from "@/pages/fnb/FnbPoItems";
import FnbPoItemDetail from "@/pages/fnb/FnbPoItemDetail";
import FnbPoLocations from "@/pages/fnb/FnbPoLocations";
import FnbPoReorder from "@/pages/fnb/FnbPoReorder";
import FnbPoStockMatrix from "@/pages/fnb/FnbPoStockMatrix";
import FnbPoPurchaseOrders from "@/pages/fnb/FnbPoPurchaseOrders";
import FnbPoNewPO from "@/pages/fnb/FnbPoNewPO";
import FnbPoAutoSuggest from "@/pages/fnb/FnbPoAutoSuggest";
import FnbPoDraftReview from "@/pages/fnb/FnbPoDraftReview";
import FnbPoManualCreate from "@/pages/fnb/FnbPoManualCreate";
import FnbPoBom from "@/pages/fnb/FnbPoBom";
import FnbPoBomDetail from "@/pages/fnb/FnbPoBomDetail";
import FnbPoBarStock from "@/pages/fnb/FnbPoBarStock";
import FnbPoMerchandise from "@/pages/fnb/FnbPoMerchandise";
import FnbPoLocationDetail from "@/pages/fnb/FnbPoLocationDetail";
import FnbPoOrderingRules from "@/pages/fnb/FnbPoOrderingRules";
import FnbPoMultidateActivity from "@/pages/fnb/FnbPoMultidateActivity";
import AdminLanding from "@/pages/admin/AdminLanding";
import ApiKeys from "@/pages/admin/ApiKeys";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

// Phase 8.1 — OAuth callback race condition fix.
//
// When Emergent bounces the user back to `<origin>/#session_id=…`,
// we MUST render the AuthCallback component BEFORE any other route
// gets a chance to react to the initial `null` user state (which
// would redirect to /login and lose the session_id). Detecting the
// hash inside a useEffect is too late — it runs after the first
// render, by which point ProtectedRoute has already navigated away.
// So we check the hash synchronously here in the render body.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Landing />} />
        {/* Amazon-tool subtree: isolated error boundary so an Amazon-side
            render crash does NOT take down the F&B tool. The classic
            failure mode (passing a non-string to toast.error / rendering
            an object as a React child) is now caught here. */}
        <Route
          path="/tools/amazon-invoices"
          element={
            <ErrorBoundary
              scope="amazon-tool"
              fallbackTitle="Amazon tool hit an error"
              fallbackHint="Refresh the page. If this keeps happening, open /api/system/health in a new tab and share the output with the admin."
            >
              <RunHistory />
            </ErrorBoundary>
          }
        />
        <Route
          path="/tools/amazon-invoices/new"
          element={
            <ErrorBoundary scope="amazon-tool" fallbackTitle="Amazon tool hit an error">
              <Run />
            </ErrorBoundary>
          }
        />
        <Route
          path="/tools/amazon-invoices/settings"
          element={
            <ErrorBoundary scope="amazon-tool" fallbackTitle="Amazon tool hit an error">
              <Settings />
            </ErrorBoundary>
          }
        />
        <Route
          path="/tools/amazon-invoices/:id"
          element={
            <ErrorBoundary scope="amazon-tool" fallbackTitle="Amazon tool hit an error">
              <RunDetail />
            </ErrorBoundary>
          }
        />

        {/* Phase 9.0 — F&B Stock & PO Release Tool. */}
        <Route path="/tools/fnb-po" element={<FnbPoLanding />} />
        <Route path="/tools/fnb-po/master-data" element={<FnbPoUploads />} />
        {/* Phase 9.4 legacy redirect — the maintenance page was renamed
            "Master Data" but bookmarks to /uploads should still work. */}
        <Route
          path="/tools/fnb-po/uploads"
          element={<Navigate to="/tools/fnb-po/master-data" replace />}
        />
        <Route path="/tools/fnb-po/reorder" element={<FnbPoReorder />} />
        {/* Phase 9.7.10 — Stock Matrix v2 (vendor-grouped, review-gated). */}
        <Route path="/tools/fnb-po/stock-matrix" element={<FnbPoStockMatrix />} />
        {/* Phase 9.1 legacy path — same component now (v2 replaced the heatmap). */}
        <Route path="/tools/fnb-po/stock/matrix" element={<FnbPoStockMatrix />} />
        <Route path="/tools/fnb-po/items" element={<FnbPoItems />} />
        <Route path="/tools/fnb-po/items/:sku" element={<FnbPoItemDetail />} />
        <Route path="/tools/fnb-po/locations" element={<FnbPoLocations />} />
        <Route path="/tools/fnb-po/bar-stock" element={<FnbPoBarStock />} />
        <Route path="/tools/fnb-po/merchandise" element={<FnbPoMerchandise />} />
        <Route path="/tools/fnb-po/location/:location_id" element={<FnbPoLocationDetail />} />

        {/* Phase 9.7 — File-driven ordering rules master data. */}
        <Route path="/tools/fnb-po/ordering-rules" element={<FnbPoOrderingRules />} />
        {/* Phase 9.7.9 — Multidate Activity Ingest (Master Data → Admin group) */}
        <Route path="/tools/fnb-po/multidate-activity" element={<FnbPoMultidateActivity />} />

        {/* Phase 9.2 — Purchase Order lifecycle. */}
        <Route path="/tools/fnb-po/pos" element={<FnbPoPurchaseOrders />} />
        <Route path="/tools/fnb-po/pos/new" element={<FnbPoNewPO />} />
        <Route path="/tools/fnb-po/pos/auto" element={<FnbPoAutoSuggest />} />
        <Route path="/tools/fnb-po/pos/manual" element={<FnbPoManualCreate />} />
        <Route path="/tools/fnb-po/pos/:po_id" element={<FnbPoDraftReview />} />

        {/* Phase 9.3 — BOM mapping. */}
        <Route path="/tools/fnb-po/bom" element={<FnbPoBom />} />
        <Route path="/tools/fnb-po/bom/:pos_sku" element={<FnbPoBomDetail />} />

        {/* Phase 2 — Admin tools (API Keys for now, more to come). */}
        <Route
          path="/admin"
          element={
            <ErrorBoundary scope="admin" fallbackTitle="Admin tool hit an error">
              <AdminLanding />
            </ErrorBoundary>
          }
        />
        <Route
          path="/admin/api-keys"
          element={
            <ErrorBoundary scope="admin" fallbackTitle="Admin tool hit an error">
              <ApiKeys />
            </ErrorBoundary>
          }
        />

        {/* Legacy redirects — keep bookmarks alive. */}
        <Route path="/runs" element={<Navigate to="/tools/amazon-invoices" replace />} />
        <Route path="/runs/:id" element={<LegacyRunRedirect />} />
        <Route path="/run" element={<Navigate to="/tools/amazon-invoices/new" replace />} />
        <Route path="/settings" element={<Navigate to="/tools/amazon-invoices/settings" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Top-level safety net — catches any uncaught render exception
            that escapes a page-level boundary (e.g. in Layout, AuthProvider,
            route resolution). Must sit INSIDE AuthProvider so auth context
            is available to the fallback if it ever needs it; must sit
            INSIDE BrowserRouter so the fallback can offer a working
            "reload page" button. */}
        <ErrorBoundary scope="app">
          <AppRouter />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Forward /runs/:id → /tools/amazon-invoices/:id. Five lines beat a
// separate file.
function LegacyRunRedirect() {
  const path = window.location.pathname.replace("/runs/", "/tools/amazon-invoices/");
  return <Navigate to={path} replace />;
}
